import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, Copy, Globe, Info, Megaphone, Plus, RotateCw, Trash2, X } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { AdvertiserOwnerBadge } from "@/components/reassign-advertiser-popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { DomainStatusPill } from "@/components/ui/status-pill";
import { GaDashboard } from "@/components/ga-dashboard";
import { PixelsCard } from "@/components/pixels-card";
import {
    assignPage,
    deleteDomain,
    getDomain,
    reassignFresh,
    resyncKv,
    retireDomain,
    unretireDomain,
    updateDomain,
} from "@/api/domains";
import { DomainProvisioningCard } from "@/components/domain-provisioning-card";
import { listPages, listVersions } from "@/api/pages";
import { listDomainsPaged } from "@/api/domains";
import { createCampaign, listCampaignsForDomain } from "@/api/campaigns";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty";
import { apiErrorMessage } from "@/lib/api";
import { formatDate, formatRelativeTime } from "@/lib/utils";

export function DomainDetailPage(): React.ReactNode {
    const { id } = useParams<{ id: string }>();
    const domainId = Number(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const domainQuery = useQuery({
        queryKey: ["domain", domainId],
        queryFn: () => getDomain(domainId),
        enabled: Number.isFinite(domainId),
        // Auto-refresh while the domain is still provisioning.
        refetchInterval: (query) => {
            const data = query.state.data;
            if (!data) return false;
            const inFlight = [
                "PURCHASING",
                "NS_DELEGATED",
                "ZONE_ACTIVE",
                "WORKER_ATTACHED",
            ] as const;
            return (inFlight as readonly string[]).includes(data.status) ? 3000 : false;
        },
    });
    const pagesQuery = useQuery({ queryKey: ["pages"], queryFn: listPages });
    // Pool size so "Move to fresh" can be disabled with explanation when empty.
    const poolQuery = useQuery({
        queryKey: ["domains", "available-pool-count"],
        queryFn: () => listDomainsPaged({ status: "AVAILABLE", page: 0, size: 1 }),
    });
    const poolCount = poolQuery.data?.totalElements ?? 0;

    const [selectedPageId, setSelectedPageId] = React.useState<number | null>(null);
    const [selectedVersionId, setSelectedVersionId] = React.useState<number | null>(null);
    // User-touched flag — once the operator picks a page or version manually,
    // stop overwriting their selection from the server-state effect below.
    const userTouchedRef = React.useRef(false);
    const versionsQuery = useQuery({
        queryKey: ["page", selectedPageId, "versions"],
        queryFn: () => listVersions(selectedPageId as number),
        enabled: selectedPageId != null,
    });

    // Default both dropdowns to whatever's actually serving on this domain.
    // Re-runs when either the domain or its versions list changes (e.g.
    // after a publish bumps the active version).
    React.useEffect(() => {
        if (userTouchedRef.current) return;
        const d = domainQuery.data;
        if (!d) return;
        if (d.activePageId != null && selectedPageId !== d.activePageId) {
            setSelectedPageId(d.activePageId);
        }
        if (d.activePageVersionId != null && selectedVersionId !== d.activePageVersionId) {
            setSelectedVersionId(d.activePageVersionId);
        }
    }, [domainQuery.data, selectedPageId, selectedVersionId]);

    // Domain DTO now carries denormalised page name + version num (server joins
    // once). No N+1 lookup from the UI.

    const [gaCode, setGaCode] = React.useState("");
    React.useEffect(() => {
        if (domainQuery.data?.gaCode !== undefined) {
            setGaCode(domainQuery.data.gaCode ?? "");
        }
    }, [domainQuery.data?.gaCode]);

    const assignMut = useMutation({
        mutationFn: () => assignPage(domainId, selectedVersionId as number),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success("Page assigned");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Assign failed")),
    });

    const retireMut = useMutation({
        mutationFn: () => retireDomain(domainId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success("Domain retired");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Retire failed")),
    });

    const unretireMut = useMutation({
        mutationFn: () => unretireDomain(domainId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success("Domain back in rotation — assign a page to serve it");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Un-retire failed")),
    });

    const resyncMut = useMutation({
        mutationFn: () => resyncKv(domainId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            toast.success("Edge config re-published — live within ~30s");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Re-sync failed")),
    });

    const reassignMut = useMutation({
        mutationFn: () => reassignFresh(domainId),
        onSuccess: (fresh) => {
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success(`Reassigned to ${fresh.hostname}`);
            navigate(`/domains/${fresh.id}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Reassign failed")),
    });

    const deleteMut = useMutation({
        mutationFn: () => deleteDomain(domainId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            toast.success("Domain deleted");
            navigate("/domains");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const gaMut = useMutation({
        mutationFn: () => updateDomain(domainId, { gaCode: gaCode.trim() || null }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["domain", domainId] });
            toast.success("GA code updated");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Update failed")),
    });

    if (domainQuery.isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }
    const domain = domainQuery.data;
    if (!domain) {
        return (
            <>
                <PageHeader title={`Domain #${domainId}`} breadcrumbs={[{ label: "Domains", href: "/domains" }, { label: `#${domainId}` }]} />
                <div className="p-6">
                    <EmptyState
                        icon={<Globe className="h-8 w-8" />}
                        title="Domain not found"
                        description="It might have been hard-deleted, or the id is wrong."
                        action={
                            <Button asChild>
                                <Link to="/domains"><ArrowLeft className="h-4 w-4" /> Back to domains</Link>
                            </Button>
                        }
                    />
                </div>
            </>
        );
    }

    const isActive = ["ASSIGNED", "AVAILABLE", "WORKER_ATTACHED"].includes(domain.status);
    const canAssign = isActive && selectedVersionId != null && selectedVersionId !== domain.activePageVersionId;

    return (
        <>
            <PageHeader
                title={domain.hostname}
                breadcrumbs={[{ label: "Domains", href: "/domains" }, { label: domain.hostname }]}
                description={
                    <span className="flex flex-wrap items-center gap-2">
                        <DomainStatusPill status={domain.status} />
                        <AdvertiserOwnerBadge
                            entityType="domain"
                            entityId={domainId}
                            currentAdvertiserId={domain.advertiserId ?? null}
                            data-testid="domain-advertiser-badge"
                        />
                        {domain.cfZoneId && (
                            <button
                                type="button"
                                onClick={() => {
                                    void navigator.clipboard.writeText(domain.cfZoneId!);
                                    toast.success("Copied CF zone id");
                                }}
                                className="inline-flex items-center gap-1 rounded font-mono text-xs text-muted-foreground hover:text-foreground"
                                title="Copy zone id"
                            >
                                zone: {domain.cfZoneId}
                                <Copy className="h-3 w-3" />
                            </button>
                        )}
                        {(() => {
                            // Expires-in-N-days warning (orange under 30d, red under 7d).
                            if (!domain.expiresAt) return null;
                            const ms = new Date(domain.expiresAt).getTime() - Date.now();
                            const days = Math.round(ms / 86_400_000);
                            if (days > 30) return null;
                            return (
                                <span className="inline-flex items-center gap-2">
                                    <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs " +
                                        (days <= 7 ? "bg-destructive/15 text-destructive" : "bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200")}>
                                        <AlertTriangle className="h-3 w-3" />
                                        Expires in {days}d
                                    </span>
                                    <a
                                        href="https://porkbun.com/account/domainsSpeedy"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs underline underline-offset-2 hover:no-underline"
                                        title="Renew at Porkbun — automated renewal isn't available via the API yet, so renew from the Porkbun dashboard."
                                    >
                                        Renew at Porkbun
                                        <ArrowRight className="h-3 w-3" />
                                    </a>
                                </span>
                            );
                        })()}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        {domain.activePageVersionId && (
                            <Button asChild variant="outline" data-testid="domain-view-live">
                                <a href={`https://${domain.hostname}/`} target="_blank" rel="noreferrer">
                                    <ArrowRight className="h-4 w-4" />
                                    Open live URL
                                </a>
                            </Button>
                        )}
                        <Button variant="ghost" onClick={() => navigate("/domains")}>
                            <ArrowLeft className="h-4 w-4" />
                            Back to domains
                        </Button>
                    </div>
                }
            />
            <div className="p-6 grid gap-6 lg:grid-cols-2">
                {(() => {
                    // Freshly-purchased domain: show a propagation hint for ~20 min
                    // after NS delegation so users don't think traffic is broken
                    // while Cloudflare issues Universal SSL + TLD propagates.
                    if (!domain.purchasedAt) return null;
                    const ageMs = Date.now() - new Date(domain.purchasedAt).getTime();
                    if (ageMs > 25 * 60 * 1000) return null;
                    if (domain.status !== "AVAILABLE" && domain.status !== "ASSIGNED") return null;
                    return (
                        <div className="lg:col-span-2 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-900">
                            <Info className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                                <p className="font-medium">Propagation in progress</p>
                                <p className="mt-0.5 text-blue-900/80">
                                    Provisioning chain is complete, but visitors may see TLS errors for 5–20 min
                                    while the <code className="font-mono text-[11px]">.{domain.hostname.split(".").pop()}</code>{" "}
                                    registry publishes the updated nameservers and Cloudflare issues the Universal SSL
                                    certificate. Everything below is ready — just give the internet a few minutes to catch up.
                                </p>
                            </div>
                        </div>
                    );
                })()}
                <div className="lg:col-span-2">
                    <DomainProvisioningCard domainId={domainId} />
                </div>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Assigned page</CardTitle>
                        <CardDescription>
                            Which of your uploaded landing pages this domain is serving right now. Change it below —
                            KV syncs to the Cloudflare edge automatically.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="rounded-md border bg-muted/30 p-3">
                            <div className="text-xs text-muted-foreground">Currently serving</div>
                            <div className="mt-1 text-sm">
                                {domain.activePageVersionId ? (
                                    domain.activePageName ? (
                                        <span>
                                            <span className="font-medium">{domain.activePageName}</span>
                                            <span className="ml-2 text-xs text-muted-foreground">
                                                v{domain.activePageVersionNum}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            page version #{domain.activePageVersionId}
                                        </span>
                                    )
                                ) : (
                                    <span className="italic text-muted-foreground">
                                        nothing — visitors to <code className="font-mono text-xs">{domain.hostname}</code> will see the Worker's default 503
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="rounded-md border-t pt-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                                Change assignment
                            </p>
                        <div className="space-y-2">
                            <Label htmlFor="assign-page">Page</Label>
                            <Select
                                value={selectedPageId?.toString() ?? ""}
                                onOpenChange={(o) => { if (o) userTouchedRef.current = true; }}
                                onValueChange={(v) => {
                                    setSelectedPageId(Number(v));
                                    setSelectedVersionId(null);
                                }}
                                disabled={!isActive}
                            >
                                <SelectTrigger id="assign-page" data-testid="assign-page-select">
                                    <SelectValue placeholder="Choose a page…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {pagesQuery.data?.map((p) => (
                                        <SelectItem key={p.id} value={p.id.toString()}>
                                            {p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="assign-version">Version</Label>
                            <Select
                                value={selectedVersionId?.toString() ?? ""}
                                onOpenChange={(o) => { if (o) userTouchedRef.current = true; }}
                                onValueChange={(v) => setSelectedVersionId(Number(v))}
                                disabled={!isActive || selectedPageId == null}
                            >
                                <SelectTrigger id="assign-version" data-testid="assign-version-select">
                                    <SelectValue
                                        placeholder={
                                            selectedPageId == null ? "Choose a page first" : "Choose a version…"
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {versionsQuery.data?.map((v) => (
                                        <SelectItem key={v.id} value={v.id.toString()}>
                                            v{v.versionNum} — {v.status}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {canAssign && (
                            <div className="rounded-md border border-amber-300/50 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                                <p>
                                    <strong>Will replace</strong>{" "}
                                    {domain.activePageVersionNum != null ? `v${domain.activePageVersionNum}` : "current"}
                                    {" → "}
                                    {(() => {
                                        const v = versionsQuery.data?.find((x) => x.id === selectedVersionId);
                                        return v ? `v${v.versionNum}` : `version #${selectedVersionId}`;
                                    })()}
                                    {" "}on {domain.hostname}.
                                </p>
                                <p className="mt-1 text-amber-900/80 dark:text-amber-100/80">
                                    Edge cache updates within ~30s. Active visitors see the new version on their next page load.
                                </p>
                            </div>
                        )}
                        <Button
                            className="w-full"
                            data-testid="assign-button"
                            onClick={() => assignMut.mutate()}
                            disabled={!canAssign || assignMut.isPending}
                        >
                            {assignMut.isPending ? <Spinner size="sm" /> : selectedVersionId === domain.activePageVersionId ? "Already assigned" : "Assign this version"}
                        </Button>
                        {!isActive && (
                            <p className="text-xs text-muted-foreground">
                                Assignment is locked until provisioning completes.
                            </p>
                        )}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Google Analytics</CardTitle>
                        <CardDescription>
                            Injected into every HTML response served from this hostname.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="ga-code">Measurement ID</Label>
                            <Input
                                id="ga-code"
                                data-testid="ga-code-input"
                                placeholder="G-XXXXXXXXXX"
                                value={gaCode}
                                onChange={(e) => setGaCode(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => gaMut.mutate()}
                                disabled={gaMut.isPending || gaCode === (domain.gaCode ?? "")}
                                data-testid="save-ga-button"
                            >
                                {gaMut.isPending ? <Spinner size="sm" /> : "Save"}
                            </Button>
                            {(domain.gaCode || gaCode) && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setGaCode("");
                                        if (domain.gaCode) gaMut.mutate();
                                    }}
                                    disabled={gaMut.isPending}
                                    data-testid="remove-ga-button"
                                    title="Unset the measurement id"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    Remove
                                </Button>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Usually set automatically by the CREATE_GA provisioning step. Override here only if
                            you need to point this domain at an existing GA4 property.
                        </p>
                    </CardContent>
                </Card>

                <div className="lg:col-span-2">
                    <DomainCampaignsCard domainId={domainId} hostname={domain.hostname} />
                </div>

                <div className="lg:col-span-2">
                    <PixelsCard domainId={domainId} />
                </div>

                <div className="lg:col-span-2">
                    <GaDashboard
                        domainId={domainId}
                        gaCode={domain.gaCode}
                        gaPropertyId={domain.gaPropertyId}
                    />
                </div>

                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-base">Lifecycle</CardTitle>
                        <CardDescription>Metadata and destructive actions.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <dl className="grid gap-3 sm:grid-cols-2 text-sm">
                            <div>
                                <dt className="text-muted-foreground">Registrar</dt>
                                <dd>{domain.registrar}</dd>
                            </div>
                            <div>
                                <dt className="text-muted-foreground">Purchased</dt>
                                <dd>{formatDate(domain.purchasedAt)}</dd>
                            </div>
                            <div>
                                <dt className="text-muted-foreground">Expires</dt>
                                <dd>{formatDate(domain.expiresAt)}</dd>
                            </div>
                            <div>
                                <dt className="text-muted-foreground">Retired</dt>
                                <dd>
                                    {domain.retiredAt ? formatDate(domain.retiredAt) : <span className="text-muted-foreground">—</span>}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-muted-foreground">Created</dt>
                                <dd>{formatRelativeTime(domain.createdAt)}</dd>
                            </div>
                            <div>
                                <dt className="text-muted-foreground">Updated</dt>
                                <dd>{formatRelativeTime(domain.updatedAt)}</dd>
                            </div>
                        </dl>
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => reassignMut.mutate()}
                                    disabled={
                                        reassignMut.isPending ||
                                        domain.status === "RETIRED" ||
                                        domain.activePageVersionId == null ||
                                        poolCount === 0
                                    }
                                    data-testid="reassign-fresh-button"
                                    title={
                                        poolCount === 0
                                            ? "No AVAILABLE pool domain — buy one first."
                                            : "Moves the current page to an AVAILABLE pool domain and retires this one. Useful if this hostname got flagged."
                                    }
                                >
                                    {reassignMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-4 w-4" />}
                                    Move page to a fresh domain
                                    {poolCount === 0 && (
                                        <span className="text-xs text-muted-foreground">(no pool)</span>
                                    )}
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => confirm.show({
                                        title: `Retire "${domain.hostname}"?`,
                                        description: "Visitors will see a 503 from the Cloudflare edge immediately. The domain stays registered at Porkbun and can be un-retired or reassigned later.",
                                        destructive: true,
                                        confirmLabel: "Retire",
                                        "data-testid": "retire-confirm",
                                        onConfirm: async () => {
                                            await new Promise<void>((resolve, reject) => {
                                                retireMut.mutate(undefined, {
                                                    onSuccess: () => resolve(),
                                                    onError: (e) => reject(e),
                                                });
                                            });
                                        },
                                    })}
                                    disabled={retireMut.isPending || domain.status === "RETIRED" || domain.status === "FAILED"}
                                    data-testid="retire-button"
                                    title="Stops serving this domain. KV entry removed. Domain stays registered at Porkbun."
                                >
                                    {retireMut.isPending ? <Spinner size="sm" /> : "Retire"}
                                </Button>
                                {domain.status === "RETIRED" && (
                                    <Button
                                        variant="outline"
                                        onClick={() => unretireMut.mutate()}
                                        disabled={unretireMut.isPending}
                                        data-testid="unretire-button"
                                        title="Brings this domain back into rotation. The Cloudflare zone + worker survived retirement, so it starts serving again as soon as you assign a page."
                                    >
                                        {unretireMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-4 w-4" />}
                                        Bring back into rotation
                                    </Button>
                                )}
                                {(domain.status === "ASSIGNED" || domain.status === "AVAILABLE" || domain.status === "WORKER_ATTACHED") && (
                                    <Button
                                        variant="outline"
                                        onClick={() => resyncMut.mutate()}
                                        disabled={resyncMut.isPending}
                                        data-testid="resync-kv-button"
                                        title="Re-publishes this domain's edge config to Cloudflare. Use if the live page looks out of date with what's assigned here."
                                    >
                                        {resyncMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-4 w-4" />}
                                        Re-sync edge config
                                    </Button>
                                )}
                                {domain.status === "FAILED" && (
                                    <Button
                                        variant="destructive"
                                        onClick={() => confirm.show({
                                            title: `Permanently delete "${domain.hostname}"?`,
                                            description: "Removes the DB row + its provisioning job history. If this domain was already registered at Porkbun, prefer Retry provisioning above — deleting throws the row away but does NOT un-register (or refund) the domain.",
                                            destructive: true,
                                            confirmLabel: "Delete permanently",
                                            "data-testid": "domain-delete-confirm",
                                            onConfirm: async () => {
                                                await new Promise<void>((resolve, reject) => {
                                                    deleteMut.mutate(undefined, {
                                                        onSuccess: () => resolve(),
                                                        onError: (e) => reject(e),
                                                    });
                                                });
                                            },
                                        })}
                                        disabled={deleteMut.isPending}
                                        data-testid="delete-button"
                                        title="Hard-delete this failed domain row (and its provisioning job history). Only allowed in FAILED state."
                                    >
                                        {deleteMut.isPending ? <Spinner size="sm" /> : <><Trash2 className="h-4 w-4" /> Delete permanently</>}
                                    </Button>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                <strong>Move page to a fresh domain</strong> picks the oldest <code className="font-mono text-[11px]">AVAILABLE</code> domain from the pool, copies this domain's page + GA code onto it, then retires this one. Typical use: this hostname gets flagged by Facebook and you need a clean replacement without taking the campaign down.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
            {confirm.dialog}
        </>
    );
}

function DomainCampaignsCard({ domainId, hostname }: { domainId: number; hostname: string }): React.ReactNode {
    const queryClient = useQueryClient();
    const [creating, setCreating] = React.useState(false);
    const [name, setName] = React.useState("");
    const campaignsQuery = useQuery({
        queryKey: ["campaigns", "by-domain", domainId],
        queryFn: () => listCampaignsForDomain(domainId),
    });
    const createMut = useMutation({
        mutationFn: () => createCampaign({ domainId, name: name.trim() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns", "by-domain", domainId] });
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            toast.success("Campaign created");
            setName("");
            setCreating(false);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create campaign")),
    });
    const copy = (link: string | null) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(() => toast.success("Link copied"));
    };
    const campaigns = campaignsQuery.data ?? [];
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <CardTitle className="text-base">Campaigns</CardTitle>
                        <CardDescription>
                            Per-affiliate buckets that share <code>{hostname}</code> as the host. Each campaign has its own
                            shareable link with a <code>?c=</code> tag; clicks + leads roll up into per-campaign analytics.
                        </CardDescription>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setCreating(true)} data-testid="domain-new-campaign">
                        <Plus className="h-4 w-4" /> New
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {creating && (
                    <form
                        className="flex items-center gap-2 rounded-md bg-muted/30 p-2"
                        onSubmit={(e) => { e.preventDefault(); if (name.trim()) createMut.mutate(); }}
                    >
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Campaign name (e.g. Mediabuyers Q2)" autoFocus />
                        <Button type="submit" size="sm" disabled={!name.trim() || createMut.isPending}>
                            {createMut.isPending ? <Spinner size="sm" /> : "Create"}
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => { setCreating(false); setName(""); }}>
                            Cancel
                        </Button>
                    </form>
                )}
                {campaignsQuery.isLoading ? (
                    <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                ) : campaigns.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                        <Megaphone className="mx-auto mb-1 h-5 w-5" />
                        No campaigns on this domain yet.
                    </div>
                ) : (
                    <ul className="space-y-1.5">
                        {campaigns.map((c) => (
                            <li key={c.id} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
                                <Link to={`/campaigns/${c.id}`} className="font-medium hover:underline">{c.name}</Link>
                                <span className="font-mono text-[10px] text-muted-foreground">{c.slug}</span>
                                <span className={"rounded-full px-1.5 py-0.5 text-[10px] uppercase " +
                                    (c.status === "active" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300" :
                                     c.status === "paused" ? "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300" :
                                     "bg-muted text-muted-foreground")}>{c.status}</span>
                                <button
                                    type="button"
                                    onClick={() => copy(c.link)}
                                    className="ml-auto inline-flex items-center gap-1 rounded border border-border px-2 py-1 font-mono text-[10px] hover:bg-muted"
                                    title="Copy affiliate link"
                                >
                                    <Copy className="h-3 w-3" /> Copy link
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}
